"use client";

import React, { useContext, useMemo } from "react";
import styled, { ThemeContext } from "styled-components";

import Flex, { FlexProps } from "../Flex";
import Box, { BoxProps } from "../Box";
import Text from "../Text";
import Icon from "../Icon";
import ImageView from "../ImageView";
import { getColor } from "../utils";

type Props = {
  id?: string;
  title: string;
  image?: {
    style?: "rounded" | "circle" | "iconOnly";
    icon?: string;
    src?: string;
  };
  size?: "small" | "regular";
  detail?: string;
  onClick?: () => void;
  disabled?: boolean;
  selected?: boolean;
  cardStyle?: "default" | "radio" | "checkbox" | "disclosure";
} & FlexProps &
  BoxProps;

type ContainerProps = { disabled: boolean; selectable: boolean };

const Container = styled(Flex).attrs<ContainerProps>({
  borderRadius: "default",
})<ContainerProps>`
  cursor: ${props => (props.disabled && "not-allowed") || (props.selectable && "pointer")};
  transition: background-color 0.4s;
  opacity: ${props => props.disabled && "50%"};
  &:hover {
    background: ${props => props.selectable && getColor("grayLightest")};
  }
`;

const OptionCard: React.FC<Props> = ({
  id = null,
  title,
  detail = null,
  image = null,
  cardStyle = "default",
  disabled = false,
  selected = false,
  onClick = null,
  size = "regular",
  ...rest
}) => {
  function renderImage() {
    if (!image) return null;

    if (image.style === "iconOnly") {
      return (
        <Box mr="m">
          <Icon id="optioncard-icon" size="40" fill="grayDarkest" iconId={image.icon} />
        </Box>
      );
    }

    let radius;
    if (image.style === "rounded") radius = "default";
    if (image.style === "circle") radius = "50%";

    return (
      <Box borderRadius={radius} mr="m" background="grayLightest" overflow="hidden">
        <ImageView
          id="optioncard-image"
          width="40px"
          height="40px"
          icon={image.icon}
          iconFill="grayDarkest"
          src={image.src}
        />
      </Box>
    );
  }
  const theme = useContext(ThemeContext);
  const accentColor = (theme && theme.primaryColor) || "primary";

  function renderAccessory() {
    switch (cardStyle) {
      case "radio":
        return (
          <Icon
            ml="s"
            id="optioncard-accessory"
            fill={accentColor}
            iconId={selected ? "radio-checked" : "radio-unchecked"}
          />
        );
      case "checkbox":
        return (
          <Icon
            ml="s"
            id="optioncard-accessory"
            fill={accentColor}
            iconId={selected ? "checkbox-checked" : "checkbox-unchecked"}
          />
        );
      case "disclosure":
        return <Icon ml="s" id="optioncard-accessory" fill="grayDarker" iconId="arrow-right" />;
      default:
        return null;
    }
  }

  const ariaAttributes = useMemo(() => {
    switch (cardStyle) {
      case "radio":
        return {
          role: "radio",
          "aria-checked": selected,
          "aria-disabled": disabled,
        };
      case "checkbox":
        return {
          role: "checkbox",
          "aria-checked": selected,
          "aria-disabled": disabled,
        };
      case "disclosure":
        return {
          role: "button",
        };
    }
  }, [cardStyle, disabled, selected]);

  const role = useMemo(() => {
    switch (cardStyle) {
      case "radio":
        return "radio";
      case "checkbox":
        return "checkbox";
      case "disclosure":
        return "button";
    }
  }, [cardStyle]);

  return (
    <Container
      data-testid={id ?? "option-card"}
      mb="8px"
      px="16px"
      role={role}
      minHeight={size === "small" ? null : "80px"}
      borderRadius="default"
      py={size === "small" ? "12px" : "16px"}
      alignItems="center"
      border={selected && cardStyle !== "checkbox" ? `1px solid ${accentColor}` : "default"}
      disabled={disabled}
      selectable={!disabled && cardStyle !== "default"}
      onClick={disabled ? null : onClick}
      {...ariaAttributes}
      {...rest}>
      {renderImage()}
      <Flex flex="10" flexDirection="column">
        <Text id="optioncard-title" font="ui.regularBold">
          {title}
        </Text>
        {detail && (
          <Text id="optioncard-detail" font={size === "small" ? "ui.small" : "ui.regular"}>
            {detail}
          </Text>
        )}
      </Flex>
      {renderAccessory()}
    </Container>
  );
};

export default OptionCard;
